/**
 * World Map player flow: hub → pan/zoom map → block panel → claim / view profile.
 */

import { apiPath } from "../../lib/apiPath";
import { paintBannerCardArt } from "../banner-card-preview";
import {
  paintProfilePhoto,
  type ProfilePhotoConfig,
  type ProfileShowcaseCosmetic,
  type ProfileShowcaseData,
} from "../profile-showcase";
import { fetchWorldMap, fetchWorldMapOwnerInfo, postWorldMapClaim } from "./api";
import { mountWorldMapView, type WorldMapTreeApi } from "./map-ui";
import type { WorldMapBlockView, WorldMapSnapshot } from "./types";

export type WorldMapRuntimeHooks = {
  isSignedIn: () => boolean;
  openSignIn: () => void;
  showWorldMapScreen: () => void;
  hideWorldMapScreen: () => void;
  getWorldMapMount: () => HTMLElement | null;
  getWorldMapPanel: () => HTMLElement | null;
  returnToHub: () => void;
  openPublicPlayerProfile: (userId: string) => void | Promise<void>;
  playModeCardBeep: () => void;
  setReturnBtnLabel: (label: string) => void;
  resolveCosmeticDisplay: (
    item: ProfileShowcaseCosmetic,
    cursorColor: string,
  ) => Promise<{ imageData: string; animationFrames: string[] | null }>;
};

let hooks: WorldMapRuntimeHooks | null = null;
let active = false;
let mapApi: WorldMapTreeApi | null = null;
let snapshot: WorldMapSnapshot | null = null;
let selected: WorldMapBlockView | null = null;
let busy = false;

export function mountWorldMapRuntime(h: WorldMapRuntimeHooks): void {
  hooks = h;
}

export function isWorldMapActive(): boolean {
  return active;
}

function clearMap() {
  mapApi?.destroy();
  mapApi = null;
}

function clearPanel() {
  const panel = hooks?.getWorldMapPanel();
  if (!panel) return;
  panel.replaceChildren();
  panel.classList.add("hidden");
  panel.setAttribute("aria-hidden", "true");
}

function openPanelShell() {
  const panel = hooks?.getWorldMapPanel();
  if (!panel) return;
  panel.classList.remove("hidden");
  panel.setAttribute("aria-hidden", "false");
}

function canDispute(block: WorldMapBlockView): boolean {
  if (!snapshot || !block.ownerUserId) return false;
  const myBest = Math.floor(Number(snapshot.myBests[block.modeId]) || 0);
  return myBest > block.ownerRecordAtClaim;
}

function myBestFor(modeId: string): number {
  return Math.floor(Number(snapshot?.myBests?.[modeId]) || 0);
}

async function fetchPublicProfileLite(userId: string): Promise<ProfileShowcaseData | null> {
  try {
    const r = await fetch(
      `${apiPath("/api/profile")}?${new URLSearchParams({ userId, include: "equipped" })}`,
      { credentials: "include", headers: { Accept: "application/json" } },
    );
    if (!r.ok) return null;
    return (await r.json()) as ProfileShowcaseData;
  } catch {
    return null;
  }
}

async function renderNeutralPanel(block: WorldMapBlockView) {
  const panel = hooks?.getWorldMapPanel();
  if (!panel || !hooks) return;
  panel.replaceChildren();
  openPanelShell();

  const region = snapshot?.regions.find((r) => r.modeId === block.modeId);
  const title = document.createElement("h2");
  title.className = "worldMapPanelTitle";
  title.textContent = "Neutral block";
  panel.appendChild(title);

  const sub = document.createElement("p");
  sub.className = "accessHint worldMapPanelSub";
  sub.textContent = `Available in ${region?.label || block.modeId}. Anyone can claim it — your mode record locks onto the block.`;
  panel.appendChild(sub);

  const scoreLine = document.createElement("p");
  scoreLine.className = "worldMapPanelCost";
  scoreLine.innerHTML = `Your ${region?.label || block.modeId} record: <strong>${myBestFor(block.modeId)}</strong>`;
  panel.appendChild(scoreLine);

  const actions = document.createElement("div");
  actions.className = "worldMapPanelActions";
  const claimBtn = document.createElement("button");
  claimBtn.type = "button";
  claimBtn.className = "btn green";
  claimBtn.textContent = "Claim block";
  const signedIn = hooks.isSignedIn();
  claimBtn.disabled = busy || !signedIn;
  if (!signedIn) claimBtn.title = "Sign in to claim";
  claimBtn.onclick = () => void doClaim(block);
  actions.appendChild(claimBtn);
  panel.appendChild(actions);

  if (!signedIn) {
    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = "Sign in to claim territory.";
    panel.appendChild(hint);
  }
}

function appendModeBestsList(panel: HTMLElement, bests: Record<string, number>) {
  const wrap = document.createElement("div");
  wrap.className = "worldMapPanelScores";
  const heading = document.createElement("h3");
  heading.textContent = "Mode records";
  wrap.appendChild(heading);
  const list = document.createElement("ul");
  list.className = "worldMapPanelScoreList";
  const entries = Object.entries(bests)
    .map(([modeId, v]) => [modeId, Math.floor(Number(v) || 0)] as const)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (!entries.length) {
    const empty = document.createElement("li");
    empty.textContent = "None yet";
    list.appendChild(empty);
  } else {
    for (const [modeId, v] of entries) {
      const li = document.createElement("li");
      const region = snapshot?.regions.find((r) => r.modeId === modeId);
      li.textContent = `${region?.label || modeId}: ${v}`;
      list.appendChild(li);
    }
  }
  wrap.appendChild(list);
  panel.appendChild(wrap);
}

async function paintOwnerHero(
  panel: HTMLElement,
  profile: ProfileShowcaseData,
  cursorColor: string,
) {
  const cosmetics = Array.isArray(profile.cosmetics) ? profile.cosmetics : [];
  const banner = cosmetics.find((c) => c.category === "banner" && c.equipped) || null;

  const hero = document.createElement("div");
  hero.className = "worldMapPanelHero";

  const photoWrap = document.createElement("div");
  photoWrap.className = "worldMapPanelPhoto";
  const photoCanvas = document.createElement("canvas");
  photoCanvas.width = 96;
  photoCanvas.height = 96;
  photoCanvas.className = "worldMapPanelPhotoCanvas";
  photoWrap.appendChild(photoCanvas);
  hero.appendChild(photoWrap);

  const bannerWrap = document.createElement("div");
  bannerWrap.className = "worldMapPanelBanner";
  const bannerCanvas = document.createElement("canvas");
  bannerCanvas.width = 320;
  bannerCanvas.height = 80;
  bannerCanvas.className = "worldMapPanelBannerCanvas";
  bannerWrap.appendChild(bannerCanvas);
  hero.appendChild(bannerWrap);
  panel.appendChild(hero);

  try {
    await paintProfilePhoto(
      photoCanvas,
      (profile.profilePhoto as ProfilePhotoConfig | null) || null,
      cosmetics,
      (item, color) => hooks!.resolveCosmeticDisplay(item, color),
      cursorColor,
    );
  } catch {
    const ctx = photoCanvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#1a2438";
      ctx.fillRect(0, 0, 96, 96);
    }
  }

  if (banner?.imageData) {
    paintBannerCardArt(bannerCanvas, banner.imageData, banner.animationFrames, "idle");
  } else {
    const ctx = bannerCanvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#152033";
      ctx.fillRect(0, 0, bannerCanvas.width, bannerCanvas.height);
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.font = "10px 'Press Start 2P', monospace";
      ctx.textAlign = "center";
      ctx.fillText("No banner", bannerCanvas.width / 2, bannerCanvas.height / 2 + 4);
    }
  }
}

async function renderOwnerPanel(block: WorldMapBlockView) {
  const panel = hooks?.getWorldMapPanel();
  if (!panel || !hooks || !block.ownerUserId) return;
  panel.replaceChildren();
  openPanelShell();

  const loading = document.createElement("p");
  loading.className = "accessHint";
  loading.textContent = "Loading…";
  panel.appendChild(loading);

  const [profile, ownerInfo] = await Promise.all([
    fetchPublicProfileLite(block.ownerUserId),
    fetchWorldMapOwnerInfo(block.ownerUserId).catch(() => null),
  ]);

  if (selected?.id !== block.id) return;
  panel.replaceChildren();

  const displayName =
    profile?.user?.displayName || ownerInfo?.user?.displayName || block.ownerDisplayName || "Player";
  const title = document.createElement("h2");
  title.className = "worldMapPanelTitle";
  title.textContent = displayName;
  panel.appendChild(title);

  const region = snapshot?.regions.find((r) => r.modeId === block.modeId);
  const sub = document.createElement("p");
  sub.className = "accessHint worldMapPanelSub";
  sub.textContent = `Owns this block in ${region?.label || block.modeId} · record locked at ${block.ownerRecordAtClaim}`;
  panel.appendChild(sub);

  if (profile) {
    await paintOwnerHero(panel, profile, profile.cursorColor || ownerInfo?.cursorColor || "#ffffff");
  }

  appendModeBestsList(panel, ownerInfo?.modeBests || {});

  const actions = document.createElement("div");
  actions.className = "worldMapPanelActions";

  const profileBtn = document.createElement("button");
  profileBtn.type = "button";
  profileBtn.className = "btn";
  profileBtn.textContent = "View Profile";
  profileBtn.onclick = () => {
    void hooks?.openPublicPlayerProfile(block.ownerUserId!);
  };
  actions.appendChild(profileBtn);

  const disputable = canDispute(block);
  const signedIn = hooks.isSignedIn();
  if (disputable && signedIn) {
    const claimBtn = document.createElement("button");
    claimBtn.type = "button";
    claimBtn.className = "btn green";
    claimBtn.textContent = "Take block";
    claimBtn.disabled = busy;
    claimBtn.title = `Your record (${myBestFor(block.modeId)}) beats the locked ${block.ownerRecordAtClaim}`;
    claimBtn.onclick = () => void doClaim(block);
    actions.appendChild(claimBtn);
  } else if (block.ownerUserId && signedIn && !disputable) {
    const locked = document.createElement("p");
    locked.className = "accessHint worldMapPanelLocked";
    const need = block.ownerRecordAtClaim + 1;
    const mine = myBestFor(block.modeId);
    locked.textContent = `Locked — need a higher ${region?.label || block.modeId} record than ${block.ownerRecordAtClaim}. Yours: ${mine}${mine < need ? ` (need ${need}+)` : ""}.`;
    panel.appendChild(locked);
  }

  panel.appendChild(actions);
}

async function selectBlock(block: WorldMapBlockView) {
  selected = block;
  mapApi?.setSelectedId(block.id);
  hooks?.playModeCardBeep();
  if (!block.ownerUserId) await renderNeutralPanel(block);
  else await renderOwnerPanel(block);
}

async function doClaim(block: WorldMapBlockView) {
  if (!hooks || busy) return;
  if (!hooks.isSignedIn()) {
    hooks.openSignIn();
    return;
  }
  busy = true;
  try {
    const result = await postWorldMapClaim(block.id);
    if (snapshot) {
      if (result.myBests) {
        snapshot.myBests = { ...snapshot.myBests, ...result.myBests };
      }
      const idx = snapshot.blocks.findIndex((b) => b.id === result.block.id);
      if (idx >= 0) snapshot.blocks[idx] = result.block;
      else snapshot.blocks.push(result.block);
      mapApi?.setData(snapshot.regions, snapshot.blocks);
    }
    selected = result.block;
    mapApi?.setSelectedId(result.block.id);
    await renderOwnerPanel(result.block);
  } catch (e) {
    const err = e as { data?: { error?: string }; message?: string };
    const panel = hooks.getWorldMapPanel();
    if (panel) {
      const msg = document.createElement("p");
      msg.className = "accessHint worldMapPanelError";
      const code = err.data?.error || err.message || "claim_failed";
      if (code === "record_not_high_enough")
        msg.textContent = "Your record is not high enough to take this block.";
      else if (code === "already_own_block") msg.textContent = "You already own this block.";
      else msg.textContent = "Could not claim this block.";
      panel.appendChild(msg);
    }
  } finally {
    busy = false;
  }
}

async function refreshMap() {
  if (!hooks) return;
  const mount = hooks.getWorldMapMount();
  if (!mount) return;
  const data = await fetchWorldMap();
  snapshot = data;
  if (!data.regions.length) {
    clearMap();
    mount.innerHTML = "";
    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = data.message || "World Map is unavailable right now.";
    mount.appendChild(hint);
    return;
  }
  clearMap();
  mapApi = mountWorldMapView({
    root: mount,
    regions: data.regions,
    blocks: data.blocks,
    selectedId: selected?.id ?? null,
    onBlockSelect: (b) => {
      void selectBlock(b);
    },
  });
}

export async function openWorldMapFlow(): Promise<void> {
  if (!hooks) return;
  active = true;
  busy = false;
  selected = null;
  clearPanel();
  hooks.setReturnBtnLabel("Return");
  hooks.showWorldMapScreen();
  hooks.playModeCardBeep();
  try {
    await refreshMap();
  } catch {
    const mount = hooks.getWorldMapMount();
    if (mount) {
      mount.innerHTML = "";
      const hint = document.createElement("p");
      hint.className = "accessHint";
      hint.textContent = "Could not load World Map. Try again later.";
      mount.appendChild(hint);
    }
  }
}

export function closeWorldMapFlow(): void {
  clearMap();
  clearPanel();
  active = false;
  selected = null;
  snapshot = null;
  busy = false;
  hooks?.hideWorldMapScreen();
}

export function leaveWorldMapToHub(): void {
  closeWorldMapFlow();
  hooks?.returnToHub();
}

export function handleWorldMapReturn(): boolean {
  if (!active) return false;
  if (selected) {
    selected = null;
    mapApi?.setSelectedId(null);
    clearPanel();
    return true;
  }
  leaveWorldMapToHub();
  return true;
}
